import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { CLINIC_ON } from './edition.js';

/**
 * Em que contexto a Início abre (estudo da fase E e T7.29). Pessoal: a central (ou o palco
 * cheio, se a pessoa escolheu nos Ajustes). Clínica: o dia da clínica, para quem tem uma (no
 * horário de atendimento, sozinha). O seletor do alto troca à mão e a escolha vale até o fim
 * do dia; nos Ajustes dá para fixar. Tudo neste aparelho. (O antigo "Painel" virou a central.)
 */
export type HomeMode = 'pessoal' | 'clinica';
export type HomeModeRule = 'horario' | HomeMode;

type Hours = { day: number; open: string; close: string };
type ClinicLike = { name?: string; public?: { hours?: Hours[] } } | null | undefined;

const RULE_KEY = 'secretario.inicio.regra';
const PICK_KEY = 'secretario.inicio.escolha';
const EVENT = 'secretario:inicio';

const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const write = (key: string, value: string | null) => {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Sem armazenamento (janela anônima): a troca vale só até recarregar.
  }
  window.dispatchEvent(new Event(EVENT));
};

const localDay = (now: Date) =>
  `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
const minutes = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};

/** A clínica está atendendo agora, pelo horário cadastrado? */
export function clinicOpenNow(hours: Hours[], now = new Date()): boolean {
  const day = now.getDay();
  const at = now.getHours() * 60 + now.getMinutes();
  return hours.some((row) => row.day === day && at >= minutes(row.open) && at < minutes(row.close));
}

const isMode = (value: string | null | undefined): value is HomeMode =>
  value === 'pessoal' || value === 'clinica';

export function readHomeRule(): HomeModeRule {
  const value = read(RULE_KEY);
  return isMode(value) ? value : 'horario';
}

export function setHomeRule(rule: HomeModeRule) {
  write(RULE_KEY, rule === 'horario' ? null : rule);
  write(PICK_KEY, null);
}

/** Escolha à mão, até o fim do dia (a chave e o "Voltar ao Pessoal" da mesa). */
export function chooseHomeMode(mode: HomeMode) {
  write(PICK_KEY, `${localDay(new Date())}|${mode}`);
}

/** A escolha à mão de hoje (some na virada do dia). */
function todayPick(now = new Date()): HomeMode | null {
  const raw = read(PICK_KEY);
  if (!raw) return null;
  const [day, mode] = raw.split('|');
  return day === localDay(now) && isMode(mode) ? mode : null;
}

export function resolveHomeMode(
  rule: HomeModeRule,
  pick: HomeMode | null,
  hours: Hours[],
  now = new Date(),
  hasClinic = true,
): HomeMode {
  // Sem clínica, "Clínica" não existe: vale o Pessoal.
  const usable = (mode: HomeMode) => (mode === 'clinica' && !hasClinic ? 'pessoal' : mode);
  if (pick) return usable(pick);
  if (rule !== 'horario') return usable(rule);
  return hasClinic && clinicOpenNow(hours, now) ? 'clinica' : 'pessoal';
}

/** O estado da Início para a casca do app. `available` é falso para quem não tem clínica. */
export function useHomeMode() {
  const clinicQ = useQuery({
    queryKey: ['clinic'] as const,
    queryFn:
      import.meta.env.VITE_CLINIC === 'off'
        ? async () => null
        : () => import('./api/clinic.js').then((m) => m.getClinic()),
    staleTime: 60_000,
    retry: false,
    enabled: CLINIC_ON,
  });
  const clinic = clinicQ.data as ClinicLike;
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const bump = () => setTick((value) => value + 1);
    window.addEventListener(EVENT, bump);
    // A cada minuto: a clínica abre e fecha sozinha conforme o horário.
    const timer = window.setInterval(bump, 60_000);
    return () => {
      window.removeEventListener(EVENT, bump);
      window.clearInterval(timer);
    };
  }, []);
  void tick;
  const available = Boolean(clinic);
  const rule = readHomeRule();
  const pick = todayPick();
  const mode = resolveHomeMode(rule, pick, clinic?.public?.hours ?? [], new Date(), available);
  const choose = useCallback((next: HomeMode) => chooseHomeMode(next), []);
  // Os lugares do seletor: o Pessoal e, para quem tem, a clínica. Sem negócio, a barra fica limpa.
  const options: HomeMode[] = available ? ['pessoal', 'clinica'] : ['pessoal'];
  const showSwitch = available;
  return { available, mode, choose, options, showSwitch, clinicName: clinic?.name ?? null };
}
